import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ReservationReturn } from '../clients/rust-api-client';
import { memberReservationService } from '../clients/ApiClients';
import AppSidebar from '../AppSidebar';
import { Navbar } from '../Components/Catalyst/navbar';
import { SidebarLayout } from '../Components/Catalyst/sidebar-layout';
import { Heading, Subheading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import HomeSelector from '../Components/Custom/HomeSelector';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import LoadingSpinner from '../LoadingSpinner';
import { useMember } from '../Hooks/MemberContext';
import ReservationRow from './ReservationRow';
import MyWaitlistSection from './MyWaitlistSection';
import { reservationErrorMessage } from './ruleMessages';

interface Props {
  currentPage: string;
}

type Scope = 'upcoming' | 'past';

/**
 * The household's bookings: what is coming up (confirmed and awaiting
 * approval) and what is behind them (completed, cancelled, declined,
 * expired). Per home, like everything else money-shaped here.
 */
const MyReservationsPage: React.FC<Props> = ({ currentPage }) => {
  const { homes, selectedHome, loading: memberLoading } = useMember();
  const homeId = selectedHome?.home_id;
  const [scope, setScope] = useState<Scope>('upcoming');
  const [rows, setRows] = useState<ReservationReturn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!homeId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    memberReservationService
      .listMyReservations({ homeId, scope, perPage: 100 })
      .then(({ data }) => {
        if (!cancelled) setRows(data.reservations);
      })
      .catch((err) => {
        if (!cancelled) {
          setRows([]);
          setError(reservationErrorMessage(err, "Couldn't load your reservations."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [homeId, scope]);

  if (memberLoading) return <LoadingSpinner />;

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={currentPage} />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Heading>My reservations</Heading>
        <Link
          to="/amenities"
          className="text-sm text-sky-700 hover:underline dark:text-sky-400"
        >
          Book an amenity
        </Link>
      </div>

      {homes.length === 0 ? (
        <Subheading className="mt-2">
          Your account is not linked to a home yet. Please contact your
          association manager.
        </Subheading>
      ) : (
        <>
          <div className="mt-2">
            <HomeSelector />
          </div>

          <div className="mt-6 inline-flex rounded-lg bg-zinc-100 p-1 dark:bg-zinc-800" role="tablist">
            {(['upcoming', 'past'] as Scope[]).map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={scope === s}
                onClick={() => setScope(s)}
                className={clsx(
                  'min-h-[2.5rem] rounded-md px-4 text-sm font-medium',
                  scope === s
                    ? 'bg-white text-zinc-950 shadow dark:bg-zinc-700 dark:text-white'
                    : 'text-zinc-600 dark:text-zinc-400'
                )}
              >
                {s === 'upcoming' ? 'Upcoming' : 'Past'}
              </button>
            ))}
          </div>

          <ErrorAlert className="mt-4">{error}</ErrorAlert>

          {loading ? (
            <LoadingSpinner />
          ) : rows.length === 0 && !error ? (
            <Text className="mt-6">
              {scope === 'upcoming'
                ? 'No upcoming reservations.'
                : 'No past reservations yet.'}
            </Text>
          ) : (
            <ul className="mt-4 space-y-2">
              {rows.map((r) => (
                <ReservationRow key={r.id} reservation={r} />
              ))}
            </ul>
          )}

          {homeId && scope === 'upcoming' && <MyWaitlistSection homeId={homeId} />}
        </>
      )}
    </SidebarLayout>
  );
};

export default MyReservationsPage;
